/**
 * Subscription helpers. State comes from PostgreSQL via /subscriptions/me.
 *
 * Statuses are compared case-insensitively, and the server's "TRIALING" is
 * treated the same as the older local "TRIAL", so a cached value written by a
 * previous build does not silently read as no-access.
 */

const TRIAL_STATUSES = ['TRIAL', 'TRIALING'];

/** User-facing name for the one-time in-app trial plan. */
export const FREE_TRIAL_PLAN_NAME = 'Free Trial with Limited Features';

export function hasAppAccess(access) {
  if (!access || typeof access !== 'object') return false;
  if (access.active === true) return true;
  const status = String(access.status || '').toUpperCase();
  return status === 'ACTIVE' || TRIAL_STATUSES.includes(status);
}

export function isTrialAccess(access) {
  if (!access || typeof access !== 'object') return false;
  const status = String(access.status || '').toUpperCase();
  return TRIAL_STATUSES.includes(status);
}

/**
 * True once a cancellation has been requested but the paid period is still
 * running — the user keeps full access until it ends.
 */
export function isCancelPending({ access, subscription } = {}) {
  if (access && access.cancelAtPeriodEnd) return true;
  if (subscription && subscription.cancelAtPeriodEnd) return true;

  const status = String((subscription && subscription.status) || '').toUpperCase();
  if (status !== 'CANCELLED' && status !== 'CANCELED') return false;

  const endsAt = subscription && subscription.endsAt;
  if (!endsAt) return false;
  const date = new Date(endsAt);
  return !Number.isNaN(date.getTime()) && date.getTime() > Date.now();
}

export function canStartTrial(subscription) {
  if (!subscription) return true;
  if (subscription.trialUsed) return false;
  const status = String(subscription.status || '').toUpperCase();
  return status === 'NONE' || status === '';
}

/** True when the one-time free trial has lapsed and the user has no paid access. */
export function isTrialExpired({ subscription, access } = {}) {
  if (!subscription || !subscription.trialUsed) return false;
  if (hasAppAccess(access)) return false;

  const planCode = String(subscription.planCode || '').toUpperCase();
  const paidCodes = ['ANNUAL', 'SIX_MONTHS', 'THREE_MONTHS'];
  if (paidCodes.includes(planCode)) return false;
  if (subscription.endsAt && planCode !== 'FREE_TRIAL') return false;

  return true;
}

export function formatPlanPrice(plan) {
  if (!plan) return '';
  const amount = Number(plan.amountInr || plan.amount || 0);
  return `₹${amount.toLocaleString('en-IN')}`;
}

export function formatDuration(months) {
  const m = Number(months) || 0;
  if (m === 1) return '1 month';
  return `${m} months`;
}

export function permissionsFromAccess(access) {
  if (!access) return [];
  if (Array.isArray(access.permissions)) return access.permissions;
  if (access.fullAccess) {
    return [
      'PERM_DSHAPE_PLOT',
      'PERM_DSHAPE_ORIENT',
      'PERM_HORSESHOE',
      'PERM_CIRCULAR',
      'PERM_SHAFT',
      'PERM_FULL_ACCESS',
    ];
  }
  return [];
}

export function hasPermission(access, perm) {
  const list = permissionsFromAccess(access);
  return list.includes('PERM_FULL_ACCESS') || list.includes(perm);
}

export function formatEndDate(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Whole days from now until `value`, never negative. */
export function daysRemaining(value, now = new Date()) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const ms = date.getTime() - now.getTime();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

const STATUS_LABELS = {
  ACTIVE: 'Active',
  TRIAL: FREE_TRIAL_PLAN_NAME,
  TRIALING: FREE_TRIAL_PLAN_NAME,
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
  CANCELED: 'Cancelled',
  NONE: 'Not subscribed',
};

/**
 * One plain-English reading of what the user currently has, for the account
 * menu. `access` is the server's verdict and is trusted over `subscription`,
 * whose status can still say ACTIVE on a period that has already run out.
 */
export function describeAccess({ access, subscription, plans } = {}) {
  const status = String(
    (access && access.status) || (subscription && subscription.status) || 'NONE'
  ).toUpperCase();

  const active = hasAppAccess(access);
  const trial = TRIAL_STATUSES.includes(status);
  const cancelPending = isCancelPending({ access, subscription });
  const endsAt =
    (access && access.endsAt) ||
    (trial ? subscription && subscription.trialEnds : subscription && subscription.endsAt);

  const planCode = (access && access.planCode) || (subscription && subscription.planCode);
  const known = Array.isArray(plans) ? plans.find((p) => p.id === planCode) : null;

  let planName;
  if (trial) planName = FREE_TRIAL_PLAN_NAME;
  else if (known) planName = known.name;
  else if (planCode) planName = String(planCode);
  else planName = 'No plan';

  const statusLabel = STATUS_LABELS[status] || 'Not subscribed';

  let detail = '';
  if (active && endsAt) {
    const left = daysRemaining(endsAt);
    const until = formatEndDate(endsAt);
    if (trial) {
      detail =
        left === 0 ? 'Ends today' : `${left} day${left === 1 ? '' : 's'} left · until ${until}`;
    } else if (cancelPending) {
      detail = `Cancels on ${until}`;
    } else {
      detail = `Valid until ${until}`;
    }
  } else if (status === 'EXPIRED' && endsAt) {
    detail = `Expired on ${formatEndDate(endsAt)}`;
  } else if (!active) {
    detail = 'Subscribe to keep using Xlog3D';
  }

  return { active, trial, cancelPending, status, statusLabel, planName, detail, endsAt };
}
